// OWNED BY the broadcast implementation agent (see ../BroadcastTab.tsx ownership note).
//
// Data layer of the customer-page broadcast tab (דיוור). Ports the legacy
// sent-messages business logic WITHOUT importing the legacy components:
//
// - Per-contact query semantics from ContactTableNestedRowSentMessages:
//   dashboard-users-v2/{email}/sentMessages where sentTo == <phone>. The legacy
//   row added `orderBy sentTime desc` (which requires a composite index); here
//   we run equality-only queries and sort client-side, so NO composite index is
//   needed for any candidate format.
// - Phone-format drift handling from BulkMessagingLog.runPhoneSearch: sentTo has
//   been observed as E.164 with '+', E.164 without '+', and Israeli local
//   0-prefixed. Each candidate runs as its own query with per-candidate
//   failure isolation and doc-id dedup, exactly like the legacy search.
// - Three-shape timestamp defensiveness from BulkMessagingLog.toMillis:
//   createdTime is a Firestore Timestamp on new writes (converted to Date by
//   GenericConverter), but a raw ISO string on legacy REST-written rows;
//   sentTime is an ISO string by convention but is treated just as defensively.
// - Attachment conventions (image/video extension lists) from
//   utils/contacts/bulkMessage/new.ts (IMAGE_EXTENSIONS / VIDEO_EXTENSIONS) —
//   the send record carries no mimeType, only mediaLink + customFileName, so
//   the kind is derived from the file name (falling back to the storage URL).
//
// Honesty contract of this tab: only SUCCESSFUL extension sends are persisted
// (both broadcast surfaces write through FirestoreContext.saveSentMessage only
// on operationStatus === "success"). There is no failed/delivered/read state
// anywhere in the data — every record shown here is simply "sent".

import { format, isValid } from "date-fns";
import { he } from "date-fns/locale";
import { db, firebaseApp } from "firebaseConfig";
import { GenericConverter } from "utils/firestoreConverters";
import { fetchCached, setCached } from "../../data/sessionCache";
import type {
  BulkMessageSentMessage,
  RealEstateContact
} from "../../../../../../@types/sharedSchema";

// ---------------------------------------------------------------------------
// Cache keys (shared-key convention: "<tabId>:<contact.id>")
// ---------------------------------------------------------------------------

export const contactBroadcastKey = (contactId: string): string => `broadcast:${contactId}`;

export const BROADCAST_MAX_AGE_MS = 60_000;

// Safety valve per candidate query — a single contact never legitimately has
// thousands of sends; mirrors the legacy SEARCH_LIMIT cap.
const MAX_MESSAGES_PER_CANDIDATE = 500;

// ---------------------------------------------------------------------------
// Phone-format candidates (ported from BulkMessagingLog.runPhoneSearch)
// ---------------------------------------------------------------------------

/**
 * All storage variants a contact's phone may appear as in `sentTo`: the raw
 * contact id / callerPhoneNumber verbatim, E.164 with and without '+', and the
 * Israeli local 0-prefixed form. Legacy contact ids landed in sentTo verbatim
 * via NewBulkMessageDialog, hence the drift.
 */
export function buildSentToCandidates(contact: RealEstateContact): string[] {
  const sources = [contact?.id, contact?.contactId];
  const candidates = new Set<string>();

  for (const raw of sources) {
    if (!raw || typeof raw !== "string") continue;
    candidates.add(raw);

    const digits = raw.replace(/\D/g, "");
    if (!digits) continue;

    candidates.add(digits);
    // '+<digits>' is only a real stored shape for international digits — no
    // write site ever produced '+0…' (the legacy search never built it either),
    // so skip that impossible candidate instead of wasting a query + listener.
    if (!digits.startsWith("0")) candidates.add(`+${digits}`);
    if (digits.startsWith("972")) candidates.add(`0${digits.slice(3)}`);
    if (digits.startsWith("0")) {
      candidates.add(`972${digits.slice(1)}`);
      candidates.add(`+972${digits.slice(1)}`);
    }
  }

  return Array.from(candidates);
}

// ---------------------------------------------------------------------------
// Timestamp handling (ported from BulkMessagingLog.toMillis — three shapes)
// ---------------------------------------------------------------------------

export function toMillis(value: unknown): number {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "string") {
    const t = new Date(value).getTime();
    return Number.isFinite(t) ? t : 0;
  }
  const anyValue = value as { toDate?: () => Date; seconds?: number };
  if (typeof anyValue.toDate === "function") return anyValue.toDate().getTime();
  if (typeof anyValue.seconds === "number") return anyValue.seconds * 1000;
  return 0;
}

/** sentTime (ISO string by convention) first, createdTime as fallback. */
export function getSentMessageTimeMs(message: BulkMessageSentMessage): number {
  return toMillis(message.sentTime) || toMillis(message.createdTime);
}

const sortNewestFirst = (messages: BulkMessageSentMessage[]): BulkMessageSentMessage[] =>
  [...messages].sort((a, b) => getSentMessageTimeMs(b) - getSentMessageTimeMs(a));

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

const sentMessagesQueryForCandidate = (userEmail: string, candidate: string) =>
  // NOTE: the real collection name is `sentMessages` — the sharedSchema constant
  // SENT_MESSAGES_COLLECTION_NAME ('sent-messages') is wrong/unused; every real
  // read/write path uses `sentMessages`. Do not "fix" this to the constant.
  db
    .collection(`dashboard-users-v2/${userEmail}/sentMessages`)
    .withConverter(GenericConverter<BulkMessageSentMessage>())
    .where("sentTo", "==", candidate)
    .limit(MAX_MESSAGES_PER_CANDIDATE);

const mergeDedupById = (
  perCandidate: BulkMessageSentMessage[][]
): BulkMessageSentMessage[] => {
  const seen = new Set<string>();
  const merged: BulkMessageSentMessage[] = [];
  for (const batch of perCandidate) {
    for (const message of batch) {
      const key = message.id ?? `${message.sentTo}|${message.sentTime}|${message.message}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(message);
    }
  }
  return sortNewestFirst(merged);
};

/**
 * One-shot fetch of every send record for this contact across all sentTo
 * format candidates. Per-candidate failures are isolated (one broken query
 * doesn't blank the whole list) — same resilience as the legacy phone search.
 * A TOTAL failure (every candidate query rejected — offline, rules/token
 * hiccup) throws instead of resolving [], so the session cache records an
 * error rather than a bogus "no broadcasts" empty list and the tab's error
 * snackbar is reachable.
 */
export async function fetchSentMessagesForContact(
  contact: RealEstateContact
): Promise<BulkMessageSentMessage[]> {
  const userEmail = firebaseApp.auth().currentUser?.email;
  if (!userEmail) return [];

  const candidates = buildSentToCandidates(contact);
  if (candidates.length === 0) return [];

  let failedCount = 0;
  let lastError: unknown = null;

  const perCandidate = await Promise.all(
    candidates.map(async (candidate) => {
      try {
        const snapshot = await sentMessagesQueryForCandidate(userEmail, candidate).get();
        return snapshot.docs.map((doc) => doc.data());
      } catch (error) {
        console.error(`Broadcast tab: sentMessages query failed for "${candidate}"`, error);
        failedCount += 1;
        lastError = error;
        return [] as BulkMessageSentMessage[];
      }
    })
  );

  if (failedCount === candidates.length) {
    throw lastError instanceof Error
      ? lastError
      : new Error(`Broadcast tab: all ${candidates.length} sentMessages queries failed`);
  }

  return mergeDedupById(perCandidate);
}

/** Fire-and-forget warm-up used by preloadBroadcastTab (row hover / page mount). */
export function preloadContactBroadcast(contact: RealEstateContact | null | undefined): void {
  const contactId = contact?.id;
  if (!contactId) return;
  void fetchCached(
    contactBroadcastKey(contactId),
    () => fetchSentMessagesForContact(contact as RealEstateContact),
    { maxAgeMs: BROADCAST_MAX_AGE_MS }
  ).catch(() => undefined);
}

/**
 * Live listeners: one light equality-filtered onSnapshot per sentTo candidate,
 * merged (doc-id dedup) into the session cache so the tab body AND the
 * tab-strip badge update in real time when a new broadcast lands. To avoid
 * clobbering a cached full list with a partial one, nothing is written until
 * every candidate delivered its first snapshot (same pattern as the calls tab).
 */
export function subscribeToContactSentMessages(contact: RealEstateContact): () => void {
  const contactId = contact?.id;
  const userEmail = firebaseApp.auth().currentUser?.email;
  if (!contactId || !userEmail) return () => undefined;

  const candidates = buildSentToCandidates(contact);
  if (candidates.length === 0) return () => undefined;

  let cancelled = false;
  let anyCandidateSucceeded = false;
  const byCandidate = new Map<string, BulkMessageSentMessage[]>();
  const pendingFirstSnapshot = new Set(candidates);

  const writeMergedIfReady = (): void => {
    if (pendingFirstSnapshot.size > 0 || cancelled) return;
    // If EVERY candidate listener errored (auth-token refresh failure, rules
    // redeploy), writing the merged — fully empty — result would clobber a
    // good cached list (e.g. from the preload fetch) and silently flip the
    // tab + badge to "no broadcasts". Keep the last good value instead.
    if (!anyCandidateSucceeded) return;
    setCached(contactBroadcastKey(contactId), mergeDedupById(Array.from(byCandidate.values())));
  };

  const unsubscribers = candidates.map((candidate) =>
    sentMessagesQueryForCandidate(userEmail, candidate).onSnapshot(
      (snapshot) => {
        anyCandidateSucceeded = true;
        byCandidate.set(
          candidate,
          snapshot.docs.map((doc) => doc.data())
        );
        pendingFirstSnapshot.delete(candidate);
        writeMergedIfReady();
      },
      (error) => {
        // Per-candidate listener failures contribute no messages — same
        // isolation as the one-shot fetch.
        console.error(`Broadcast tab: listener failed for "${candidate}"`, error);
        byCandidate.set(candidate, []);
        pendingFirstSnapshot.delete(candidate);
        writeMergedIfReady();
      }
    )
  );

  return () => {
    cancelled = true;
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  };
}

// ---------------------------------------------------------------------------
// Attachment kind (mimeType-aware via file name — the record has no mimeType)
// ---------------------------------------------------------------------------

export type BroadcastAttachmentKind = "none" | "image" | "video" | "file";

// Ported from utils/contacts/bulkMessage/new.ts (IMAGE_EXTENSIONS /
// VIDEO_EXTENSIONS + the document MIME allow-list) — kept local so the tab
// doesn't import the legacy send-flow module.
const IMAGE_EXTENSIONS = new Set(["png", "gif", "jpeg", "jpg"]);
const VIDEO_EXTENSIONS = new Set(["avi", "mp4", "mpeg", "mpg", "webm", "3gp"]);
const DOCUMENT_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "psd",
  "ai",
  "eps",
  "txt",
  "csv"
]);

const extractExtension = (name: string | undefined): string => {
  if (!name) return "";
  const clean = name.split("?")[0].split("#")[0];
  const lastDot = clean.lastIndexOf(".");
  if (lastDot === -1 || lastDot === clean.length - 1) return "";
  return clean.slice(lastDot + 1).toLowerCase();
};

const extensionFromMediaLink = (mediaLink: string): string => {
  try {
    // Firebase Storage download URLs carry the (encoded) object path in the
    // URL pathname — decode it before reading the extension.
    const pathname = decodeURIComponent(new URL(mediaLink).pathname);
    return extractExtension(pathname);
  } catch {
    return extractExtension(mediaLink);
  }
};

/**
 * Attachment kind for a send record. customFileName is the reliable source
 * (mediaLink is a tokenized storage URL); unknown extensions fall back to an
 * image ATTEMPT — the legacy row always tried rendering mediaLink as an image
 * with a broken-image fallback, and the row component keeps that fallback.
 */
export function getAttachmentKind(message: BulkMessageSentMessage): BroadcastAttachmentKind {
  if (!message.mediaLink) return "none";
  const extension =
    extractExtension(message.customFileName) || extensionFromMediaLink(message.mediaLink);
  if (VIDEO_EXTENSIONS.has(extension)) return "video";
  if (DOCUMENT_EXTENSIONS.has(extension)) return "file";
  if (IMAGE_EXTENSIONS.has(extension)) return "image";
  return "image"; // unknown → try image, row falls back to a file chip onError
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/** First non-empty line of the message — the row "title" (real sends have no title field). */
export function getMessageFirstLine(message: BulkMessageSentMessage): string {
  const firstLine = (message.message ?? "")
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line.length > 0);
  return firstLine ?? "";
}

const isSameCalendarDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** "היום 20:00" / "אתמול 18:40" / "12.7 · 20:00" (+ short year off-year) — mock s27 style. */
export function formatSentWhen(message: BulkMessageSentMessage): string {
  const timeMs = getSentMessageTimeMs(message);
  if (!timeMs) return "";

  const date = new Date(timeMs);
  const now = new Date();
  const time = `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

  if (isSameCalendarDay(date, now)) return `היום ${time}`;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameCalendarDay(date, yesterday)) return `אתמול ${time}`;

  const sameYear = date.getFullYear() === now.getFullYear();
  const dayMonth = `${date.getDate()}.${date.getMonth() + 1}`;
  const dateLabel = sameYear ? dayMonth : `${dayMonth}.${date.getFullYear() % 100}`;
  return `${dateLabel} · ${time}`;
}

export type BroadcastMonthGroup = {
  key: string;
  label: string;
  messages: BulkMessageSentMessage[];
};

/**
 * Groups an already-sorted (newest-first) list into month buckets, preserving
 * order. Records without a parsable date collect into a trailing group.
 */
export function groupSentMessagesByMonth(
  messages: BulkMessageSentMessage[]
): BroadcastMonthGroup[] {
  const groups: BroadcastMonthGroup[] = [];
  const byKey = new Map<string, BroadcastMonthGroup>();
  let unknownGroup: BroadcastMonthGroup | null = null;

  for (const message of messages) {
    const timeMs = getSentMessageTimeMs(message);
    const date = new Date(timeMs);

    if (!timeMs || !isValid(date)) {
      if (!unknownGroup) unknownGroup = { key: "unknown", label: "ללא תאריך", messages: [] };
      unknownGroup.messages.push(message);
      continue;
    }

    const key = `${date.getFullYear()}-${date.getMonth()}`;
    let group = byKey.get(key);
    if (!group) {
      group = { key, label: format(date, "LLLL yyyy", { locale: he }), messages: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.messages.push(message);
  }

  if (unknownGroup) groups.push(unknownGroup);
  return groups;
}

/** Card-header caption, e.g. "5 הודעות · אחרונה 12.7 · 20:00". */
export function getBroadcastHeaderCaption(messages: BulkMessageSentMessage[]): string {
  const total = messages.length;
  const totalLabel = total === 1 ? "הודעה אחת" : `${total} הודעות`;
  const lastWhen = messages.length > 0 ? formatSentWhen(messages[0]) : "";
  return lastWhen ? `${totalLabel} · אחרונה ${lastWhen}` : totalLabel;
}
